import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@mui/material";

// UI Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Services
import BillingService from "services/billing-service";

function PaymentFail() {
  const navigate = useNavigate();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [validFail, setValidFail] = useState(false);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10; // 10 attempts * 1s = 10 seconds timeout

    // [FIX] Proactively mark the session as failed if session_id is in URL
    const params = new URLSearchParams(window.location.search);
    const sessionIdFromUrl = params.get("session_id");

    const markAsFailedImmediately = async () => {
      if (sessionIdFromUrl) {
        try {
          console.log("🚩 Proactively marking session as failed:", sessionIdFromUrl);
          await BillingService.updateBillingRecord(sessionIdFromUrl, {
            status: "Fail",
            display_result: true,
          });
        } catch (err) {
          console.error("❌ Failed to proactively mark session as failed:", err.message);
        }
      }
    };

    const checkBillingFailure = async () => {
      try {
        const billingResponse = await BillingService.getLatestBillingRecord();
        const billing = billingResponse.billing;

        console.log(`🧾 Checking billing failure [Attempt ${attempts + 1}/${maxAttempts}]:`, billing?.status);

        // 1. Check Failure Status
        if (billing && billing.status === "Fail" && billing.display_result === true) {
          setValidFail(true);
          setShowSnackbar(true);

          await BillingService.updateBillingRecord(billing.session_id, { display_result: false });
          console.log("✅ Billing record updated to hide fail result next time.");
        }
        // 2. If Initial/Pending, Retry
        else if (billing && billing.status === "Initial" && attempts < maxAttempts) {
          console.log("⏳ Billing is still Initial. Retrying...");
          attempts++;
          setTimeout(checkBillingFailure, 1000); // Poll every 1 second
        }
        // 3. Robustness: If it actually Succeeded, redirect to success page
        else if (billing && billing.status === "Success" && billing.display_result === true) {
          console.log("🚀 Billing actually succeeded! Redirecting to success page...");
          navigate("/payment/success");
        }
        // 4. Timeout or already shown
        else {
          console.warn("⚠️ Billing record invalid for fail page, or already shown. Redirecting to profile...");
          navigate("/profile");
        }
      } catch (err) {
        console.error("❌ Error checking billing failure:", err.message);
        // If error, maybe retry a few times?
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkBillingFailure, 1000);
        } else {
          navigate("/profile");
        }
      }
    };

    // Run both
    markAsFailedImmediately().then(() => {
      checkBillingFailure();
    });
  }, [navigate]);

  if (!validFail) return null; // Don't render anything if not valid

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <Card
          sx={{
            p: 4,
            maxWidth: 460,
            width: "90%",
            borderRadius: "16px",
            textAlign: "center",
            backgroundColor: "rgba(255,255,255,0.95)", // Glass-ish
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          }}
        >
          <MDTypography variant="h1" sx={{ mb: 1, fontSize: "4rem !important" }}>
            🚫
          </MDTypography>

          <MDTypography variant="h4" fontWeight="bold" gutterBottom color="error">
            Payment Failed
          </MDTypography>

          <MDTypography variant="body2" color="text" sx={{ mb: 3 }}>
            Unfortunately, your payment could not be processed.
            Please try again or contact support.
          </MDTypography>

          <MDButton
            variant="contained"
            color="accent"
            size="large"
            onClick={() => navigate("/profile")}
            sx={{ px: 4, py: 1.5, fontWeight: 600, width: "100%" }}
          >
            Return to Profile
          </MDButton>
        </Card>
      </MDBox>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          Payment failed. Please try again.
        </Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
}

export default PaymentFail;
